import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../Command';
import { cn } from '../../lib/utils';
import Checkbox from '../../components/Checkbox';
import React from 'react';
import { DataTableFilterOption, Operator, Option } from './types';

interface DataTableAdvancedFacetedFilterProps {
  title?: string;
  options: Option[];
  selectedValues: Set<string>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<DataTableFilterOption[]>>;
  selectedOperator: Operator;
  selectedOption: DataTableFilterOption;
  fieldType: 'boolean' | 'stringArr' | 'numberArr';
}

export function AdvancedFacetedFilter({
  title,
  options,
  selectedValues,
  setSelectedOptions,
  selectedOperator,
  selectedOption,
  fieldType,
}: DataTableAdvancedFacetedFilterProps) {
  return (
    <Command className="p-1">
      <div className="flex h-9 w-full rounded-lg border border-neutral-700 bg-transparent px-3 py-1 text-sm shadow-sm [&_[cmdk-input-wrapper]]:border-0 [&_[cmdk-input-wrapper]]:px-0">
        <CommandInput placeholder={title} className="h-full border-0 pl-0 ring-0" autoFocus />
      </div>
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup className="px-0">
          {options.map((option) => {
            const isSelected = selectedValues.has(option.value);

            return (
              <CommandItem
                key={option.value}
                // The row is named explicitly rather than from its own content. Naming the
                // checkbox below (see the comment there) makes it an embedded control inside
                // a name-from-content element, and dom-accessibility-api and the browsers
                // disagree about what such a control contributes — locally it turned this
                // row's computed name into "Active Active" and broke every
                // getByRole('option', { name }) query in this component's suite and its
                // consumers'. Pinning the name here makes it deterministic and independent of
                // both the checkbox and `option.icon`. NOT cmdk's `value` prop, which is its
                // filtering key and is derived from the text content on purpose.
                aria-label={option.label}
                onSelect={() => {
                  if (fieldType === 'boolean') {
                    // Clear other selections and only select the current one
                    selectedValues.clear();
                    selectedValues.add(option.value);
                  } else {
                    if (isSelected) {
                      selectedValues.delete(option.value);
                    } else {
                      selectedValues.add(option.value);
                    }
                  }

                  const filterValues = Array.from(selectedValues);

                  setSelectedOptions((prev) =>
                    prev.map((item) =>
                      // Match by id, not by field name — multiple filters on the same field must not share values
                      item.id === selectedOption.id
                        ? {
                            ...item,
                            filterValues,
                            filterOperator: selectedOperator?.value,
                          }
                        : item,
                    ),
                  );
                }}
              >
                {/* If the field type is boolean, we don't need to show the checkbox*/}
                {fieldType !== 'boolean' && (
                  // The row carries the visible text, but this checkbox is a SEPARATE node in
                  // the accessibility tree and it used to reach AT unnamed — 14 identical
                  // "checkbox, not checked" in the Channels & Accounts list (WCAG SC 4.1.2).
                  // The name goes on the INPUT via `inputProps`, which SwitchBase spreads last
                  // (SwitchBase.tsx:139), and it is `option.label` verbatim, so SC 2.5.3
                  // label-in-name holds for free. Same shape as ProviderDropdownFilter.tsx:347.
                  //
                  // NOT SwitchBase's own `label` prop: it renders <label htmlFor={name}>
                  // (SwitchBase.tsx:146) while the input's id comes from the separate `id` prop
                  // (:130), so unless a caller passes both as the same string it emits a label
                  // pointing at nothing. That is a real latent bug in the shared primitive, but
                  // it has 47 call sites — wrong place to fix it, and the reason we do not push
                  // this name down into Checkbox/SwitchBase either.
                  //
                  // Deliberately NOT aria-hidden + tabIndex={-1} on this checkbox, even though
                  // an interactive descendant of role="option" is invalid ARIA on paper: cmdk's
                  // aria-selected on the row tracks keyboard HIGHLIGHT, not selection (see the
                  // aria-selected:bg-accent-dark styling in Command.tsx:116), so hiding the box
                  // would delete the only thing reporting WHICH values are ticked.
                  <Checkbox
                    className={cn(
                      '!mr-2 !p-0',
                      isSelected ? 'bg-primary text-primary-foreground' : 'opacity-20 [&_svg]:invisible',
                    )}
                    checked={isSelected}
                    inputProps={{ 'aria-label': option.label }}
                  />
                )}
                {option.icon ? option.icon : null}
                <span>{option.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
        {selectedValues.size > 0 && fieldType !== 'boolean' && (
          <>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setSelectedOptions((prev) =>
                    prev.map((item) =>
                      item.id === selectedOption.id
                        ? {
                            ...item,
                            filterValues: [],
                            filterOperator: selectedOperator?.value,
                          }
                        : item,
                    ),
                  );
                  selectedValues.clear();
                }}
                className="cursor-pointer justify-center rounded-md text-center"
              >
                Clear filters
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );
}
